import { SustainabilityMatterId } from '@upright/common/model/input/materiality-assessment/types-matters';
import { FinaFxComparisonItemId } from '../../output/materiality/csrd-company-results/financial-effects/types';

export enum MaterialityActualness {
  ACTUAL = 'A',
  POTENTIAL = 'P',
}

export enum MaterialityType {
  FINANCIAL = 'f',
  IMPACT = 'i',
}

/**
 * Keep up-to-date with @link https://linear.app/upright/document/csrd-version-schema-3727a817ab9e
 */
export enum CsrdVersionSchema {
  /**
   * The first data format used in the initial customer deliveris until 16.05.2024.
   */
  FIRST = 1,
  /**
   * Data format separating product level and impact level results. Used until X.05.2024 (TODO: Fill the date)
   */
  IMPACT_FIRST = 2,
  /**
   * Data format introducing IRO level materiality where only material IRO's are considered
   */
  IRO_THRESHOLD = 3,
}

export const allMaterialityTypes = Object.values(MaterialityType);

export enum MaterialityValence {
  POSITIVE = 'P',
  NEGATIVE = 'N',
}

export enum FinancialMaterialityType {
  RISK = 'r',
  OPPORTUNITY = 'o',
}

/**
 * Driver / Stakeholder
 */
export enum FinancialDriver {
  INVESTORS = 'Investors',
  EMPLOYEES = 'Employees',
  REGULATORS = 'Regulators',
  CONSUMERS = 'Consumers',
  NATURE = 'Nature',
  SUPPLIERS = 'Suppliers',
}

export enum FinancialItem {
  COST_OF_CAPITAL = 'Cost of capital',
  SGA_AND_RD_COSTS = 'SG&A and R&D costs',
  OTHER_OPERATING_EXPENSES = 'Other operating expenses',
  CAPEX = 'CapEx',
  REVENUE = 'Revenue',
  TANGIBLE_ASSETS = 'Tangible assets',
  COST_OF_REVENUE = 'Cost of revenue',
  EMPLOYEE_BENEFITS_EXPENSES = 'Employee benefits expenses',
}

export enum Recurrence {
  RECURRING = 'recurring',
  ONE_OFF = 'one-off',
}

/**
 * Transition vs. physical risk/opportunity classification (FE 2.0 RO structures sheet).
 * The sheet has exactly these two options.
 */
export enum TransitionPhysical {
  TRANSITION = 'Transition',
  PHYSICAL = 'Physical',
}

export const allMaterialityValences = [MaterialityValence.NEGATIVE, MaterialityValence.POSITIVE];

export enum MaterialityTimeHorizon {
  SHORT_TERM = 'st',
  MEDIUM_TERM = 'md',
  LONG_TERM = 'lt',
  UNKNOWN = 'u',
}

export const timeHorizonOrder = [
  MaterialityTimeHorizon.SHORT_TERM,
  MaterialityTimeHorizon.MEDIUM_TERM,
  MaterialityTimeHorizon.LONG_TERM,
  MaterialityTimeHorizon.UNKNOWN,
];

export const TimeHorizonWithoutUnknown = [
  MaterialityTimeHorizon.LONG_TERM,
  MaterialityTimeHorizon.MEDIUM_TERM,
  MaterialityTimeHorizon.SHORT_TERM,
];

export enum MaterialityValueChain {
  ALL = 'a',
  INTERNAL = 'i',
  UPSTREAM = 'u',
  UPSTREAM_AND_INTERNAL = 'ui',
  DOWNSTREAM = 'd',
  DOWNSTREAM_AND_INTERNAL = 'di',
  UPSTREAM_AND_DOWNSTREAM = 'ud',
  UNKNOWN = 'unknown',
}

export enum ProductLevelMaterialityResult {
  NO_TRIGGERS = 'ns',
  NONE_TRIGGERED = 'nd',
  MATERIAL = 'm',
}

export enum CompanyLevelMaterialityResult {
  /**
   * No triggers found for the matter
   */
  NO_TRIGGERS = 'ns',

  /**
   * No triggers triggered
   */
  NONE_TRIGGERED = 'nd',

  /**
   * Some triggers triggered, considered material
   */
  MATERIAL = 'm',
}

export type ByMaterialityValences<T> = {
  [valence in MaterialityValence]: T;
};

export type BySomeMaterialityValences<T> = Partial<ByMaterialityValences<T>>;

export type ByFinancialMaterialityType<T> = {
  [type in FinancialMaterialityType]: T;
};

export type CompanyLevelFmResult = {
  matterId: SustainabilityMatterId;
  valence: MaterialityValence;
  timeHorizons: MaterialityTimeHorizon[];
  materialityLevel: FinancialMaterialityLevel;
  materialityScore: number;
  materialIroCount: number | undefined; // Only from CsrdVersionSchema 3 onwards
  type: FinancialMaterialityType;
  isMaterial: boolean;
};

export type CompanyLevelImResult = {
  matterId: SustainabilityMatterId;
  valence: MaterialityValence;
  timeHorizons: MaterialityTimeHorizon[];
  valueChains: MaterialityValueChain[];
  actualness: MaterialityActualness[];
  severityLevel: SeverityLevel;
  severityScore: number;
  materialIroCount: number | undefined; // Only from CsrdVersionSchema 3 onwards
  isMaterial: boolean;
};

export type CompanyLevelFeResult = {
  matterId: SustainabilityMatterId;
  valence: MaterialityValence;
  timeHorizons: MaterialityTimeHorizon[];
  type: FinancialMaterialityType;
  materialIroCount: number | undefined; // Only from CsrdVersionSchema 3 onwards
  delta: number;
  effect: number;
  financialItemId: FinaFxComparisonItemId;
  materialityLevel: FinancialMaterialityLevel;
  isMaterial: boolean;
};
export enum CsrdIntermediateResultAggregationMethod {
  SUM = 'sum',
  /**
   * To the future readers: This was used in the 1st iteration of CSRD results
   * and can still most likely be found in the database
   */
  MAX = 'max',
}

/**
 * @deprecated
 */
export type CompanyIntermediateImResults = {
  matterId: SustainabilityMatterId;
  valence: MaterialityValence;
  severityLevel: SeverityLevel;
  severityScore: number;
  aggregationMethod: CsrdIntermediateResultAggregationMethod;
};

/**
 * @deprecated
 */
export type ProductIntermediateImResults = {
  matterId: SustainabilityMatterId;
  valence: MaterialityValence;
  severityLevel: SeverityLevel;
  severityScore: number;
  aggregationMethod: CsrdIntermediateResultAggregationMethod;
};

/**
 * @deprecated
 */
export type ProductLevelImResult = {
  matterId: SustainabilityMatterId;
  productCode: string;
  productRatio: number;
  imResult: ProductLevelMaterialityResult;
  imSeverityByValence: ByMaterialityValences<Severity>;
  imMaterialValences: MaterialityValence[];
};

export enum MaterialityLinkageLevel {
  NONE = 'n',
  LOW = 'l',
  MEDIUM = 'm',
  HIGH = 'h',
}

export enum FinancialMaterialityPattern {
  REN1 = 'REN-1',
  RIN1 = 'RIN1',
  RRN1 = 'RRN1',
  RCN1 = 'RCN1',
  OEN1 = 'OEN1',
  OIN1 = 'OIN1',
  OCN1 = 'OCN1',
  OEP1 = 'OEP1',
  OIP1 = 'OIP1',
  OCP1 = 'OCP1',
}

export enum GenericLevel {
  VERY_LOW = 'vl',
  LOW = 'l',
  MEDIUM = 'm',
  HIGH = 'h',
}

export const orderedGenericLevels = [GenericLevel.VERY_LOW, GenericLevel.LOW, GenericLevel.MEDIUM, GenericLevel.HIGH];

export enum SeverityLevel {
  NONE = 'n',
  LOW = 'l',
  MEDIUM = 'm',
  HIGH = 'h',
}

/**
 * Lower bounds for each impact materiality level
 */
export type ImpactThresholdByLevel = { [k in SeverityLevel]: number };

export type Severity = {
  level: SeverityLevel;
  score: number;
};

export enum FinancialMaterialityPatternType {
  RISK = 'r',
  OPPORTUNITY = 'o',
}

export enum FinancialMaterialityLevel {
  NONE = 'n',
  /**
   * Note: Only used for Probability & Magnitude and NOT for Financial materiality levels
   */
  VERY_LOW = 'vl',
  LOW = 'l',
  MEDIUM = 'm',
  HIGH = 'h',
}

/**
 * Lower bounds for each financial materiality level
 *
 * NOTE: Very low is deprecated and not used
 */
export type FinancialThresholdByLevel = Omit<
  { [k in FinancialMaterialityLevel]: number },
  FinancialMaterialityLevel.VERY_LOW
>;

export interface FinancialMaterialityLinkageForMatter {
  matter: SustainabilityMatterId;
  patternId: FinancialMaterialityPattern;
  stakeholder: FinancialDriver;
  patternType: FinancialMaterialityPatternType;
  patternDescription: string;
  valence: MaterialityValence;
  financialItem: FinancialItem;
  probability: FinancialMaterialityLevel;
  magnitude?: number;
  magnitudeLevel: FinancialMaterialityLevel;
  fineTuning: ApplicablePatternsByType;
  timeHorizon: MaterialityTimeHorizon | undefined;
}

export enum PatternType {
  PROBABILITY = 'p',
  MAGNITUDE = 'm',
}

export enum PatternFineTuningType {
  SHC = 'shc',
  COMMERCE = 'commercel',
  IMPACT_LEVEL = 'impact-level',
}

export type FineTunePattern = { [k in PatternFineTuningType]: boolean };
export type ApplicablePatternsByType = { [k in PatternType]: FineTunePattern };

export type FinancialMaterialityLinkageByMatter = {
  [matterId in SustainabilityMatterId]?: FinancialMaterialityLinkageForMatter[];
};

export interface PatternLevelFactor<T extends PatternType> {
  description: string;
  levelDelta: number;
  type: T;
}

export type ProbabilityPatternLevelFactor = PatternLevelFactor<PatternType.PROBABILITY>;
export type MagnitudePatternFactor = PatternLevelFactor<PatternType.MAGNITUDE>;

export type PatternResolver = <T extends PatternType>(
  linkage: FinancialMaterialityLinkageForMatter,
  type: T,
) => T extends PatternType.MAGNITUDE ? MagnitudePatternFactor[] : ProbabilityPatternLevelFactor[];

export type CsrdDimension = {
  matterId: SustainabilityMatterId;
  valence: MaterialityValence;
  materialityType: MaterialityType;
};

export type CsrdDimensions = { [K in keyof CsrdDimension]?: Array<CsrdDimension[K]> };
